// Performance by campaign type (PMAX / Search / Shopping / Display / Video /
// Demand Gen). The split an AM reaches for first on Google, because the campaign
// types behave so differently that a blended CPA hides more than it shows.
//
// ⚠️ IMPRESSION SHARE IS DELIBERATELY ABSENT FROM THIS TABLE, AND THE COLUMN
// PICKER CANNOT ADD IT. Only Search-network delivery reports it, so a PMAX or
// Display row would show "—" in every share column and invite the reader to
// conclude those types are performing badly in the auction rather than simply
// being unmeasured there. The competitiveness block above owns that story. The
// picker's options come from CHANNEL_BREAKDOWN_METRICS (the catalogue MINUS that
// family) and the stored selection is sanitized against the same list, so
// neither a click nor a hand-edited preference can put a share column back.
//
// Since 2026-08-02 this is a PANEL in the page's third grid region: it fills the
// box it is given and scrolls internally, so a resize means something.

import { useMemo } from 'react';
import { ColumnsMenu } from './ColumnsMenu';
import { convertGoogleMetrics } from './googleCurrency';
import type { GoogleMoneyDisplay } from './googleTrendSeries';
import { formatEnum, formatMetric } from './metricFormat';
import { metricDef } from '../../lib/googleAdsMetrics';
import { CHANNEL_BREAKDOWN_METRICS } from './tableWidgets';
import type {
  GoogleAdsChannelBreakdownRow,
  GoogleAdsMetricKey,
} from '../../types/googleAdsReporting';

export function ChannelBreakdown({
  rows,
  loading,
  columns,
  onColumnsChange,
  money,
}: {
  rows: GoogleAdsChannelBreakdownRow[] | undefined;
  loading: boolean;
  columns: GoogleAdsMetricKey[];
  onColumnsChange: (next: GoogleAdsMetricKey[]) => void;
  /** The agency display currency. Absent = native, and nothing converts. */
  money?: GoogleMoneyDisplay;
}): React.JSX.Element {
  // Converted ONCE per row here rather than per cell — and in Native mode
  // `convertGoogleMetrics` returns the block by identity, so this is a no-op.
  const shown = useMemo(
    () =>
      (rows ?? []).map((r) => ({
        ...r,
        metrics: convertGoogleMetrics(r.metrics, money?.display ?? '', money?.snapshot) ?? r.metrics,
      })),
    [rows, money?.display, money?.snapshot],
  );
  return (
    <section className="flex min-h-0 flex-col rounded-lg border border-border bg-card">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Performance by campaign type</h2>
        <ColumnsMenu
          options={CHANNEL_BREAKDOWN_METRICS.map((m) => ({
            key: m.key,
            label: m.label,
            hint: m.hint,
          }))}
          selected={columns}
          onChange={(next) => onColumnsChange(next as GoogleAdsMetricKey[])}
          title="Choose the columns shown for each campaign type"
        />
      </header>
      {loading && !rows ? (
        <div className="space-y-2 p-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-6 w-full animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">No campaigns in this scope.</p>
      ) : (
        // `reporting-table-panel-scroll` only does anything INSIDE a grid panel
        // (the rule is scoped under .reporting-chart-panel-body): there it makes
        // this region grow to the panel height and scroll, so a resize changes
        // what you can see. Outside the grid it is inert and the table is
        // content-height, as it always was.
        <div className="reporting-table-panel-scroll min-h-0 overflow-auto scroll-subtle">
          <table className="w-full text-sm">
            {/* Sticky for the same reason the other Google tables' headers are:
                once the region scrolls internally, the labels have to stay. */}
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="text-left text-xs text-muted-foreground shadow-[inset_0_-1px_0_hsl(var(--border))]">
                <th className="px-4 py-2 font-medium">Campaign type</th>
                {columns.map((key) => (
                  <th key={key} className="px-3 py-2 text-right font-medium">
                    {metricDef(key)?.label ?? key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.channelType} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-medium">{formatEnum(r.channelType)}</td>
                  {columns.map((key) => (
                    <td key={key} className="px-3 py-2 text-right tabular-nums">
                      {formatMetric(r.metrics, key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
